import Theme from "/components/Theme";
import dynamic from "next/dynamic";
import People from "/components/organisms/common/People";
import WideCTA from "/components/organisms/common/WideCTA";
import AdaptiveCriticalImage from "../components/atoms/AdaptiveCriticalImage";
import Link from "next/link";
import { useEffect } from "react";
import {
  viewport,
  colors,
  font,
  lineHeight,
} from "../styles/cssinjs/helpers/variables.js";
import { replaceHrefInAnswer } from "@components/organisms/common/SignupUrlReplacer";

const InjectChatSupport = dynamic(() =>
  import("@components/atoms/InjectChatSupport")
);
const AppStorage = dynamic(() => import("../components/AppStorage"));

let _IO = {};
let navs, currentItem;

// Side Nav Highlight Functionality
function removeActive() {
  navs.forEach((nav) => {
    nav.classList.remove("selected");
  });
}
function crawlDOM() {
  navs = document.querySelectorAll("#sidenav .faq-nav-link");
}
function observerFn() {
  _IO.options = {
    rootMargin: "0px 0px -80% 0px",
  };
  _IO.cb = (entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        removeActive();
        currentItem = entry.target.dataset.id;
        navs[currentItem - 1].classList.add("selected");
      }
    });
  };
  _IO.observer = new IntersectionObserver(_IO.cb, _IO.options);
  _IO.targets = document.querySelectorAll("#faq-content > article");
  _IO.targets.forEach((target) => {
    _IO.observer.observe(target);
  });
}

export async function getStaticProps(ctx) {
  let allData = {};
  const { locale } = ctx;
  // Page Route
  const route = "faqs";

  // Fetch data for respective pages
  const headerResponse = await fetch(
    `${process.env.JSON_PATH}/${locale}/common/header.json`
  );

  const footerResponse = await fetch(
    `${process.env.JSON_PATH}/${locale}/common/footer.json`
  );

  const marketingResponse = await fetch(
    `${process.env.JSON_PATH}/${locale}/others/${route}/marketing.json`
  );

  const contentResponse = await fetch(
    `${process.env.JSON_PATH}/${locale}/others/${route}/content.json`
  );

  // Catch Error
  if (marketingResponse.status === 404 || contentResponse.status === 404) {
    return {
      notFound: true,
    };
  }
  // Convert
  allData.header = await headerResponse.json();
  allData.footer = await footerResponse.json();
  allData.marketing = await marketingResponse.json();
  allData.content = await contentResponse.json();

  // Wrap
  const header = {
    header: allData.header,
  };
  const footer = {
    footer: allData.footer,
  };
  // Combine all data
  const data = await {
    ...header,
    ...footer,
    ...allData.marketing,
    ...allData.content,
  };

  // Return to the component
  return {
    props: {
      data,
    },
  };
}

export default function Faqs({ data, nonce }) {
  useEffect(() => {
    crawlDOM();
    observerFn();
  }, []);
  let id = "start-chat-btn";
  const { seo, header, footer, content } = data;
  // Chatsupport Trigger Fn.
  const TriggerChatSupport = () => {
    let ele = document.getElementsByClassName("chatsupport-button")[0];
    if (ele !== undefined) {
      ele.click();
      return;
    }
  };
  const handleClick = (e, id) => {
    if (id === "start-chat-btn") {
      e.preventDefault();
      TriggerChatSupport();
    } else {
      return true;
    }
  };
  const Hero = ({ data }) => {
    return (
      <section
        className={`container grid--default hero--default lg:pb-10 md:pb-5 has-align-top`}
      >
        <article className={`lg:maxw-lg text-left`}>
          <h1 className="text-xl lg:pt-5">{data.title}</h1>
          {data.hasOwnProperty("desc") &&
            data.desc.map((item, index) => {
              return (
                <p
                  className="lg:maxw-md lg:mt-1.5 lg:mb-5 md:mt-1.5 md:mb-2.5"
                  key={index}
                  dangerouslySetInnerHTML={{ __html: item }}
                />
              );
            })}
          <button
            className="btn-primary btn-lg md:w-100"
            id="start-chat-btn"
            onClick={(e) => {
              handleClick(e, id);
            }}
          >
            Get Support
          </button>
          <a href="" className="btn-secondary md:w-100 md:mt-2.5 lg:ml-5">
            <span>help@setmore.com</span>
          </a>
        </article>{" "}
        <figure className="md:mt-5 lg:maxw-lg">
          <picture>
            <AdaptiveCriticalImage
              src={data.img.src}
              alt={data.img.alt}
              mobile={data.img.prefixes.mobile}
              dpi={data.img.prefixes.dpi}
              dimensions={data.img.dimensions}
              className="img-fit"
            />
          </picture>
        </figure>
      </section>
    );
  };
  const MainContentWithSideNav = ({ data }) => {
    const ListItem = ({ data }) => {
      return (
        <ul className="faq-section">
          {data.map((item, index) => {
            return (
              <li
                key={index}
                className="faq lg:maxw-lg lg:px-3 lg:pt-3 lg:pb-4 md:pt-2.5 md:pl-2.5 md:pr-2.5 md:pb-2.5 bg-white md:mt-2.5"
              >
                <h4 className="text-md lg:mb-2 md:mb-1.5 text-grey">
                  {item.title}
                </h4>
                {Array.isArray(item.desc) ? (
                  item.desc.map((item, index) => {
                    return (
                      <p
                        className="lg:mb-3 md:mb-2"
                        key={index}
                        dangerouslySetInnerHTML={{
                          __html: replaceHrefInAnswer(item),
                        }}
                      />
                    );
                  })
                ) : (
                  <p
                    dangerouslySetInnerHTML={{
                      __html: replaceHrefInAnswer(item.desc),
                    }}
                  />
                )}
                {item.hasOwnProperty("planTable") && (
                  <table className="faq-with-table lg:mt-3 md:mt-3">
                    <thead>
                      <tr>
                        {item.planTable.labels.map((item, index) => {
                          return <th key={index}>{item}</th>;
                        })}
                      </tr>
                    </thead>
                    <tbody>
                      {item.planTable.list.map((item, index) => {
                        return (
                          <tr key={index}>
                            {item.map((item, index) => {
                              return <td key={index}>{item}</td>;
                            })}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </li>
            );
          })}
        </ul>
      );
    };
    return (
      <section className="lg:mt-10 md:mt-10 mb-10">
        <div className="lg:maxw-2xl container lg:d-fx fx--sb pos-rel ">
          <nav className="md:d-none fx--g-1 lg:maxw-xs-logo" id="sidenav">
            <ul className="faq-navigation">
              {data.sideNav.map((item, index) => {
                return (
                  <li key={index} className="lg:mb-2">
                    <a
                      href={item.href}
                      className="ff-euclid text-md faq-nav-link"
                    >
                      {item.text}
                      <span>.</span>
                    </a>
                  </li>
                );
              })}
            </ul>
          </nav>
          <div className="lg:maxw-lg faq-content" id="faq-content">
            {data.sections.map((item, index) => {
              return (
                <article
                  key={index}
                  id={item.id}
                  data-id={index + 1}
                  className={`pos-rel md:mb-2 ${
                    index === 0 ? `` : `lg:mt-15 `
                  }`}
                >
                  <input
                    className="radio lg:d-none pos-abs pos-abs--l-0 pos-abs--b-0"
                    type="radio"
                    id={`chck${index}`}
                    name="radio"
                  />
                  <label
                    className="faq-section-header md:d-block ff-euclid text-md fw-700 text-grey md:mb-2 lg:d-none"
                    htmlFor={`chck${index}`}
                  >
                    {item.label}
                  </label>
                  <ListItem data={item.list} />
                </article>
              );
            })}
          </div>
        </div>
        <style jsx>{`
          @media (min-width: ${viewport.tablet.sm}) {
            .faq-navigation {
              position: sticky;
              top: 180px;
            }
          }
        `}</style>
      </section>
    );
  };
  const FeaturesList = ({ data }) => {
    return (
      <section className="bg-light lg:pb-15 lg:pt-15 md:pt-10 md:pb-10">
        <article className="container lg:maxw-lg lg:text-center md:text-left">
          <h2
            className="lg:mb-3 md:mb-2 text-lg"
            dangerouslySetInnerHTML={{ __html: data.title }}
          ></h2>
          <p className="mx-5 lg:mb-10 md:m-0">{data.desc}</p>
        </article>
        <div className="container card--default card--feature has-four-col md:mt-5">
          {data.list.map((item, index) => {
            return (
              <Link
                key={index}
                href={item.href}
                prefetch={false}
                className="btn-outline md:mb-2.5 md:mt-0 md:pb-3 md:pl-2.5 md:pr-2 md:pt-2.5 pb-4 pl-3 pr-2 pt-3 text-left bg:hover-white"
              >
                <div className="icon-title-wrap">
                  <figure
                    dangerouslySetInnerHTML={{ __html: item.icon }}
                  ></figure>
                  <h3 className="lg:mb-3 lg:mt-3 text-brandcolor">
                    {item.title}
                  </h3>
                </div>
                <p>{item.desc}</p>
              </Link>
            );
          })}
        </div>
      </section>
    );
  };
  return (
    <Theme
      header={header}
      footer={footer}
      seo={seo}
      nonce={nonce}
      className="faq-root"
    >
      <AppStorage pageName="FAQs Page" />
      <Hero data={content.hero} />
      <MainContentWithSideNav data={content.mainContent} />
      <FeaturesList data={content.featuresList} />
      <People className="bg-light" />
      <WideCTA data={content.wideCTA} />
      <InjectChatSupport />
      <style jsx global>{`
        .chatsupport-button {
          visibility: hidden !important;
        }
      `}</style>
    </Theme>
  );
}
